// Teleporter System for Helix2000
// Blue glowing teleport pads for map travel

import { Schema, type } from '@colyseus/schema';
import type { TeleporterVisualType } from './teleporterPresentation';
export type { TeleporterVisualType } from './teleporterPresentation';

export class Teleporter extends Schema {
  @type('string') id!: string;
  @type('string') name?: string;
  /** Local authored destination label; rendered from map data, not network state. */
  targetName?: string;
  /** Directional subheader (e.g. "Western Rim", "Eastern Cavern") */
  targetSubheader?: string;
  /** Direction indicator ('west' = ◀, 'east' = ▶, 'north' = ▲, 'south' = ▼) */
  direction?: 'west' | 'east' | 'north' | 'south';
  @type('number') x!: number;
  @type('number') y!: number;
  @type('string') targetMapId!: string;
  @type('string') targetSpawnId!: string;
  @type('number') targetX?: number; // Optional coordinate override
  @type('number') targetY?: number;
  @type('boolean') isDefaultSpawn!: boolean;
  @type('boolean') requiresActivation: boolean = true;
  @type('string') activationType: 'manual' | 'auto' = 'manual';
  @type('string') visualType: TeleporterVisualType = 'hologram';
  @type('string') visualEffect: string = 'hologram';
  @type('boolean') isActive: boolean = true;
  @type('boolean') snapToGround: boolean = true;
  @type('boolean') targetSnapToGround: boolean = true;
  @type({ map: 'string' }) attributes = new Map<string, string>();
  @type('string') linkId: string = '';
  width?: number;
  height?: number;
  /**
   * Data-driven level gate. When > 1, a player below this level is BLOCKED
   * from activating this portal (warning shown instead of warp). When unset,
   * the gate falls back to the destination zone's recommended level band
   * (see `resolvePortalMinLevel` in shared/logic/portalGate.ts). 0/undefined =
   * inherit-from-zone.
   */
  @type('number') minLevel?: number;
  /**
   * Showcase override: force this portal ungated, ignoring both `minLevel` and
   * the destination zone's level band. Set only by the Biome Nexus tour pads.
   * Plain (un-synced) field — the level gate is evaluated client-side, so this
   * never needs to cross the wire.
   */
  bypassZoneGate?: boolean;
  /**
   * Three-tier gate (on-ramp design §3): opt this portal into a HARD block
   * for under-level players (default is soft-warn — warned but allowed
   * through within the warn band). Plain (un-synced) field — evaluated
   * client-side like `bypassZoneGate`. Unset → destination policy in
   * `portalGate.ts` (`coil_undercroft` is the one hard gate today).
   */
  hardGate?: boolean;
  /** Per-portal soft-warn band override (levels under the floor that still
   *  warn-through). Unset → DEFAULT_SOFT_WARN_BAND (2). */
  softWarnBand?: number;
  /** Sealed portal: drawn dim with a "— sealed" label, never travels
   *  (shared/logic/portalSeal.ts). Plain (un-synced) field. */
  sealed?: boolean;
  /** Quest gate (shared/logic/portalQuestGate.ts): the prompt names the quest
   *  until it is done. Plain (un-synced) field; the server enforces the gate. */
  requiredQuest?: string;
  /** Sealed portals only: the quest that opens it (shared/logic/portalSeal.ts). */
  unsealQuest?: string;
  /** Concealed: suppresses world-facing portal text and prompt (Chapter 07). */
  concealed?: boolean;
  /** Contextual action verb/text shown when nearby/focused (e.g. "Investigate", "Return to the road"). */
  contextualAction?: string;
  /** Concealed only: `false` hides the on-screen contextual prompt bar; Up/E still enters. Default shown. */
  contextualPrompt?: boolean;
  /** Accessible name for focus/screen readers (Chapter 07). */
  accessibleName?: string;
  /** Visual anchor for atmospheric effects (e.g. lamp light core wink for concealed landmarks). */
  lightCoreAnchor?: { x: number; y: number };
  /** Concealed portals only: distance (px) at which the contextual prompt appears. Default 220. */
  contextualRadius?: number;
  /** Concealed portals only: world points (window/door light) that swell with the discovery wink. */
  winkAnchors?: Array<{ x: number; y: number }>;

  constructor(data?: Partial<Teleporter> | TeleporterConfig) {
    super();
    if (data) {
      const { attributes, ...rest } = data as any;
      Object.assign(this, rest);
      if (attributes && !(attributes instanceof Map)) {
        this.attributes = new Map(Object.entries(attributes));
      } else if (attributes) {
        this.attributes = attributes;
      }
    }
  }
}

export interface TeleporterConfig {
  id: string;
  name?: string;
  x: number;
  y: number;
  width?: number;
  height?: number;
  targetMapId: string;
  targetSpawnId?: string;
  targetName?: string; // Optional human-readable name for target
  targetSubheader?: string; // Optional directional subheader (e.g. "Western Rim")
  direction?: 'west' | 'east' | 'north' | 'south'; // Direction indicator
  targetX?: number; // Override
  targetY?: number;
  isDefaultSpawn?: boolean;
  requiresActivation?: boolean;
  activationType?: 'manual' | 'auto';
  /** Road passages keep travel/prompt behavior without magical portal effects. */
  visualType?: TeleporterVisualType;
  visualEffect?: string;
  snapToGround?: boolean;
  targetSnapToGround?: boolean;
  attributes?: Record<string, string>;
  linkId?: string;
  /** Data-driven minimum player level required to use this portal. */
  minLevel?: number;
  /** Force this portal ungated (ignore minLevel + zone band). Showcase pads only. */
  bypassZoneGate?: boolean;
  /** Opt into a hard block for under-level players (default: soft-warn). */
  hardGate?: boolean;
  /** Per-portal soft-warn band override (default 2). */
  softWarnBand?: number;
  /** Sealed: placed and labelled, but leads nowhere (plan 23 row 54). */
  sealed?: boolean;
  /** Concealed: suppresses world-facing portal text and prompt (plan 2026-10-20 hush nook). */
  concealed?: boolean;
  /** Contextual action verb/text shown when nearby/focused (e.g. "Investigate", "Return to the road"). */
  contextualAction?: string;
  /** Concealed only: `false` hides the on-screen contextual prompt bar; Up/E still enters. Default shown. */
  contextualPrompt?: boolean;
  /** Accessible name for focus/screen readers (Chapter 07). */
  accessibleName?: string;
  /** Quest that opens this portal (lane BL); the prompt names it until done. */
  requiredQuest?: string;
  /** Sealed portals only: the quest that opens it (shared/logic/portalSeal.ts). */
  unsealQuest?: string;
  /** Authoring-only id/destination plates. Normal gameplay leaves this off. */
  showDebugLabels?: boolean;
  /** Visual anchor for atmospheric effects (e.g. lamp light core wink for concealed landmarks). */
  lightCoreAnchor?: { x: number; y: number };
  /** Concealed portals only: distance (px) at which the contextual prompt appears. Default 220. */
  contextualRadius?: number;
  /** Concealed portals only: world points (window/door light) that swell with the discovery wink. */
  winkAnchors?: Array<{ x: number; y: number }>;
}

// Default teleporter configuration
export const DEFAULT_TELEPORTER_CONFIG = {
  width: 64,
  height: 128,
  requiresActivation: true,
  activationType: 'manual',
  visualEffect: 'hologram',
  isDefaultSpawn: false
};
